import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import PlayerSearchBar from "./PlayerSearchBar";

interface StatEntry {
  label: string;
  value: number;
}

interface MatchPlayer {
  name: string;
  mlbam_id: number | null;
  team: string;
  season: string;
  pa: number;
  similarity: number;
  stats: StatEntry[];
}

interface StdDevEntry {
  label: string;
  std: number;
}

interface CompData {
  name: string;
  id: string;
  level: string;
  season: string;
  pa: number | null;
  position: string;
  bats: string;
  comparison_stats: StatEntry[];
  stat_std_devs: StdDevEntry[];
  stat_corrs: { label: string; corr: number }[];
  stat_projections: { label: string; projected_mlb: number }[];
  matches: MatchPlayer[];
}

interface StickinessStat {
  label: string;
  n: number;
  mean_aaa: number;
  mean_mlb: number;
  mean_diff: number;
  mean_abs_diff: number;
  corr: number;
}

interface StickinessData {
  stats: StickinessStat[];
  n_pairs: number;
}

// EV is the only non-% stat; everything else is a percentage
const MPH_LABELS = ["EV"];

function fmtStat(value: number, label: string): string {
  if (MPH_LABELS.includes(label)) return value.toFixed(1);
  return value.toFixed(1);
}

// Header label shown in table columns
function colHeader(label: string): string {
  if (label === "EV") return "EV (mph)";
  return label;
}

function SimBadge({ sim }: { sim: number }) {
  const bg =
    sim >= 65 ? "#1a7c1a" :
    sim >= 45 ? "#b8860b" :
    "#888";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: 1,
        fontSize: "0.75rem",
        fontWeight: "bold",
        color: "white",
        backgroundColor: bg,
        minWidth: 38,
        textAlign: "center",
      }}
    >
      {sim}%
    </Box>
  );
}

// Shared header cell style
const TH_SX = {
  color: "white",
  fontWeight: "bold",
  textAlign: "center" as const,
  whiteSpace: "nowrap" as const,
  py: 1,
  px: 1,
};

// Sticky player-name cell (shared bg logic handled inline)
const NAME_CELL_MIN_W = 150;

export default function MlbComparison(props: any) {
  const { id } = useParams();
  const [season, setSeason] = useState("");
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);
  const [data, setData] = useState<CompData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stickiness, setStickiness] = useState<StickinessData | null>(null);

  // Fetch available seasons for this player on mount
  useEffect(() => {
    if (!id) return;
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(`${base}/player-seasons/${id}/`)
      .then((r) => r.json())
      .then((d) => {
        if (d.seasons?.length) {
          // Comparison only works for hitters — filter to hitter seasons
          setAvailableSeasons(d.seasons);
          setSeason(d.latest);
        } else {
          setAvailableSeasons(["2023", "2024", "2025", "2026"]);
          setSeason("2026");
        }
      })
      .catch(() => {
        setAvailableSeasons(["2023", "2024", "2025", "2026"]);
        setSeason("2026");
      });
  }, [id, props.prod]);

  useEffect(() => {
    if (!id || !season) return;
    setLoading(true);
    setError("");
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(`${base}/mlb-comparison/${id}/${season}/`)
      .then((res) => res.json())
      .then((d) => {
        if (d.error) {
          setError(d.error);
          setData(null);
        } else {
          setData(d);
        }
        setLoading(false);
      })
      .catch((e) => {
        setError(String(e));
        setLoading(false);
      });
  }, [id, season, props.prod]);

  // Fetch stickiness data once (not player-specific)
  useEffect(() => {
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(`${base}/aaa-mlb-stickiness/`)
      .then((r) => r.json())
      .then((d) => { if (d.stats) setStickiness(d); })
      .catch(() => {});
  }, [props.prod]);

  const statLabels = data?.comparison_stats.map((s) => s.label) ?? [];

  // Prospect stat lookup for diff-shading
  const prospectMap: Record<string, number> = {};
  data?.comparison_stats.forEach((s) => {
    prospectMap[s.label] = s.value;
  });

  // Std dev lookup for z-score shading
  const stdMap: Record<string, number> = {};
  data?.stat_std_devs?.forEach((s) => {
    stdMap[s.label] = s.std;
  });

  // Stickiness correlation lookup (for column header indicators)
  const corrMap: Record<string, number> = {};
  data?.stat_corrs?.forEach((s) => {
    corrMap[s.label] = s.corr;
  });

  // Projected MLB value lookup (for projection row)
  const projMap: Record<string, number> = {};
  data?.stat_projections?.forEach((s) => {
    projMap[s.label] = s.projected_mlb;
  });

  // Shade a cell based on |val - prospect| in standard deviations
  function cellBg(val: number, label: string): string {
    const pv = prospectMap[label];
    const sd = stdMap[label];
    if (pv === undefined || !sd) return "inherit";
    const z = Math.abs(val - pv) / sd;
    if (z < 0.75) return "inherit";
    if (z < 1.5) return "#fff8e1"; // ~1 SD off — light amber
    return "#fff3e0"; // >1.5 SD — stronger amber
  }

  return (
    <div style={{ width: "100%", padding: "16px", boxSizing: "border-box", overflowX: "hidden" }}>
      <title>Prospect Savant – MLB Comparison</title>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
        <Typography variant="h5" fontWeight="bold">
          {data ? (
            <>
              <a
                href={`/player/${id}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {data.name}
              </a>
              <Typography
                component="span"
                variant="h5"
                color="text.secondary"
                fontWeight={400}
              >
                {" "}— {data.level} {data.season} MLB Comps
              </Typography>
            </>
          ) : (
            "MLB Comparison"
          )}
        </Typography>
        <FormControl sx={{ minWidth: 110 }} size="small">
          <InputLabel>Season</InputLabel>
          <Select
            value={season}
            label="Season"
            onChange={(e: SelectChangeEvent) => setSeason(e.target.value)}
          >
            {availableSeasons.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <PlayerSearchBar prod={props.prod} urlPrefix="/mlb-comparison" placeholder="Switch player…" />
      </Box>

      {data && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Approach &amp; contact profile · ≥100 PA · sorted by similarity to{" "}
          {data.name}&apos;s {data.level} numbers
        </Typography>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {data && !loading && (
        <Paper
          variant="outlined"
          square
          sx={{
            border: "#293241 solid 2px",
            boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black",
          }}
        >
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table
              size="small"
              sx={{ minWidth: 650, tableLayout: "auto" }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#293241" }}>
                  <TableCell
                    sx={{
                      ...TH_SX,
                      textAlign: "left",
                      minWidth: NAME_CELL_MIN_W,
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#293241",
                      zIndex: 2,
                    }}
                  >
                    Player
                  </TableCell>
                  <TableCell sx={TH_SX}>PA</TableCell>
                  <TableCell sx={TH_SX}>Match</TableCell>
                  {statLabels.map((l) => {
                    const corr = corrMap[l];
                    const dotColor =
                      corr === undefined ? "transparent" :
                      corr >= 0.75 ? "#4caf50" :
                      corr >= 0.55 ? "#ffc107" :
                      "#ef5350";
                    return (
                      <TableCell key={l} sx={TH_SX}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                          <span>{colHeader(l)}</span>
                          <Box
                            title={corr !== undefined ? `Stickiness r=${corr.toFixed(3)}` : ""}
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              backgroundColor: dotColor,
                              display: "inline-block",
                            }}
                          />
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Prospect row — always first, highlighted */}
                <TableRow sx={{ backgroundColor: "#3D5A80" }}>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#3D5A80",
                      zIndex: 1,
                      minWidth: NAME_CELL_MIN_W,
                      py: 0.75,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="white"
                      noWrap
                    >
                      {data.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#ABB8C8" }}>
                      {data.level} {data.season}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "center", color: "white", fontWeight: "bold", fontSize: "0.82rem" }}
                  >
                    {data.pa ?? "—"}
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "center", color: "#ABB8C8", fontSize: "0.82rem" }}
                  >
                    —
                  </TableCell>
                  {data.comparison_stats.map((s) => (
                    <TableCell
                      key={s.label}
                      sx={{
                        textAlign: "center",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "0.82rem",
                        py: 0.75,
                      }}
                    >
                      {fmtStat(s.value, s.label)}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Projected MLB row */}
                {data.stat_projections?.length > 0 && (
                  <TableRow sx={{ backgroundColor: "#1e3a5f" }}>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#1e3a5f",
                        zIndex: 1,
                        minWidth: NAME_CELL_MIN_W,
                        py: 0.75,
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold" color="white" noWrap>
                        Projected MLB
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#ABB8C8" }}>
                        avg translation
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", color: "#ABB8C8", fontSize: "0.82rem" }}>
                      —
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", color: "#ABB8C8", fontSize: "0.82rem" }}>
                      —
                    </TableCell>
                    {statLabels.map((label) => {
                      const proj = projMap[label];
                      const aaa = prospectMap[label];
                      const diff = proj !== undefined && aaa !== undefined ? proj - aaa : undefined;
                      return (
                        <TableCell
                          key={label}
                          sx={{ textAlign: "center", py: 0.75 }}
                        >
                          {proj !== undefined ? (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.2 }}>
                              <Typography sx={{ color: "white", fontWeight: "bold", fontSize: "0.82rem" }}>
                                {fmtStat(proj, label)}
                              </Typography>
                              {diff !== undefined && (
                                <Typography
                                  sx={{
                                    fontSize: "0.68rem",
                                    color: diff > 0 ? "#ff8a80" : "#69f0ae",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {diff > 0 ? "+" : ""}{diff.toFixed(1)}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography sx={{ color: "#ABB8C8", fontSize: "0.82rem" }}>—</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}

                {/* Divider row for visual separation */}
                <TableRow sx={{ height: 2, backgroundColor: "#293241" }}>
                  <TableCell colSpan={3 + statLabels.length} sx={{ p: 0 }} />
                </TableRow>

                {/* MLB comparison rows */}
                {data.matches.map((m, i) => {
                  const statMap: Record<string, number> = {};
                  m.stats.forEach((s) => {
                    statMap[s.label] = s.value;
                  });
                  const rowBg = i % 2 === 0 ? "white" : "#f9f9f9";

                  return (
                    <TableRow
                      key={i}
                      sx={{
                        backgroundColor: rowBg,
                        "&:hover": { backgroundColor: "#e8f0fe" },
                      }}
                    >
                      {/* Sticky name cell */}
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: rowBg,
                          zIndex: 1,
                          minWidth: NAME_CELL_MIN_W,
                          py: 0.75,
                          "&:hover": { backgroundColor: "#e8f0fe" },
                        }}
                      >
                        {m.mlbam_id ? (
                          <a
                            href={`https://baseballsavant.mlb.com/savant-player/${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${m.mlbam_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontWeight: "bold",
                              color: "#3D5A80",
                              textDecoration: "none",
                              fontSize: "0.82rem",
                            }}
                          >
                            {m.name}
                          </a>
                        ) : (
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            noWrap
                            fontSize="0.82rem"
                          >
                            {m.name}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {m.team}{m.season ? ` · ${m.season}` : ""}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          color: "text.secondary",
                          fontSize: "0.82rem",
                          py: 0.75,
                        }}
                      >
                        {m.pa}
                      </TableCell>

                      <TableCell sx={{ textAlign: "center", py: 0.75 }}>
                        <SimBadge sim={m.similarity} />
                      </TableCell>

                      {statLabels.map((label) => {
                        const val = statMap[label];
                        return (
                          <TableCell
                            key={label}
                            sx={{
                              textAlign: "center",
                              fontSize: "0.82rem",
                              py: 0.75,
                              backgroundColor:
                                val !== undefined
                                  ? cellBg(val, label)
                                  : "inherit",
                            }}
                          >
                            {val !== undefined ? fmtStat(val, label) : "—"}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 2, py: 1, borderTop: "1px solid #e0e0e0" }}>
            <Typography variant="caption" color="text.secondary">
              Similarity weighted by AAA→MLB stickiness correlation ·
              column dots: <Box component="span" sx={{ color: "#4caf50" }}>●</Box> r≥0.75 &nbsp;
              <Box component="span" sx={{ color: "#ffc107" }}>●</Box> r≥0.55 &nbsp;
              <Box component="span" sx={{ color: "#ef5350" }}>●</Box> r&lt;0.55 ·
              Projected MLB row = AAA stat + mean translation shift · amber shading = &gt;0.75 SD from prospect
            </Typography>
          </Box>
        </Paper>
      )}

      {/* AAA → MLB Stickiness Section */}
      {stickiness && stickiness.stats.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
            AAA → MLB Stat Stickiness
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            How well do Statcast stats translate from AAA to MLB? Based on {stickiness.n_pairs.toLocaleString()} player-season
            pairs (2023–2025) with ≥150 PA at each level. Sorted by correlation — higher means the AAA number
            predicts the MLB number more reliably.
          </Typography>
          <Paper
            variant="outlined"
            square
            sx={{
              border: "#293241 solid 2px",
              boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black",
            }}
          >
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#293241" }}>
                    <TableCell sx={{ ...TH_SX, textAlign: "left", minWidth: 100 }}>Stat</TableCell>
                    <TableCell sx={TH_SX}>Pairs (n)</TableCell>
                    <TableCell sx={TH_SX}>AAA Avg</TableCell>
                    <TableCell sx={TH_SX}>MLB Avg</TableCell>
                    <TableCell sx={TH_SX}>Mean Δ</TableCell>
                    <TableCell sx={TH_SX}>Avg |Δ|</TableCell>
                    <TableCell sx={{ ...TH_SX, minWidth: 160 }}>Correlation (r)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stickiness.stats.map((s, i) => {
                    const corr = s.corr;
                    const corrColor =
                      corr >= 0.75 ? "#1a7c1a" :
                      corr >= 0.55 ? "#b8860b" :
                      "#c0392b";
                    const barWidth = Math.max(0, Math.min(100, corr * 100));
                    const rowBg = i % 2 === 0 ? "white" : "#f9f9f9";
                    return (
                      <TableRow key={s.label} sx={{ backgroundColor: rowBg }}>
                        <TableCell sx={{ fontWeight: "bold", fontSize: "0.82rem", py: 0.75 }}>
                          {s.label}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", fontSize: "0.82rem", color: "text.secondary" }}>
                          {s.n}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", fontSize: "0.82rem" }}>
                          {s.mean_aaa.toFixed(1)}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", fontSize: "0.82rem" }}>
                          {s.mean_mlb.toFixed(1)}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.82rem",
                            color: s.mean_diff > 0 ? "#c0392b" : "#1a7c1a",
                            fontWeight: "bold",
                          }}
                        >
                          {s.mean_diff > 0 ? "+" : ""}{s.mean_diff.toFixed(1)}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", fontSize: "0.82rem" }}>
                          {s.mean_abs_diff.toFixed(1)}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: 1.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                flex: 1,
                                height: 10,
                                borderRadius: 1,
                                backgroundColor: "#e0e0e0",
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${barWidth}%`,
                                  height: "100%",
                                  backgroundColor: corrColor,
                                  borderRadius: 1,
                                }}
                              />
                            </Box>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "0.78rem",
                                fontWeight: "bold",
                                color: corrColor,
                                minWidth: 36,
                                textAlign: "right",
                              }}
                            >
                              {corr.toFixed(3)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ px: 2, py: 1, borderTop: "1px solid #e0e0e0" }}>
              <Typography variant="caption" color="text.secondary">
                Mean Δ = MLB avg − AAA avg (positive = stat rises at MLB level) ·
                Correlation r measures how predictive AAA numbers are of future MLB performance ·
                green ≥ 0.75 · yellow ≥ 0.55 · red &lt; 0.55
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </div>
  );
}
